<template>
  <section>
    <div class="d-flex justify-content-between mb-3">
      <div>
        <h2 class="mt-4">Open Positions</h2>
      </div>
      <div class="d-flex justify-content-between align-items-center gap-1">
        <slot name="close" />
      </div>
    </div>
    <div class="d-flex align-items-center mb-2">
      <div class="flex-grow-1">
        <div class="row">
          <div class="col-lg-3">
            <div class="dropdown">
              <div
                id="dropdownMenuButtonLocation"
                class="form-select"
                type="button"
                data-bs-toggle="dropdown"
                aria-expanded="false"
              >
                {{ locations.find((l) => l.codename == selectedLocation)?.name || 'Location' }}
              </div>
              <ul class="dropdown-menu" aria-labelledby="dropdownMenuButtonLocation">
                <li key="Location">
                  <a class="dropdown-item" href="#" @click="changeSelectedLocation('')"> Location </a>
                </li>
                <li v-for="location in locations" :key="location.name">
                  <a
                    class="dropdown-item"
                    href="#"
                    @click.prevent="changeSelectedLocation(location.codename)"
                  >
                    {{ location.name }}
                  </a>
                </li>
              </ul>
            </div>
          </div>
          <div class="col-lg-3">
            <div class="dropdown">
              <div
                id="dropdownMenuButtonCategory"
                class="form-select"
                type="button"
                data-bs-toggle="dropdown"
                aria-expanded="false"
              >
                {{ selectedCategory || 'Category' }}
              </div>
              <ul class="dropdown-menu" aria-labelledby="dropdownMenuButtonCategory">
                <li key="Category">
                  <a class="dropdown-item" href="#"  @click="changeSelectedCategory('')"> Category </a>
                </li>
                <li v-for="(term, i) in careerCategories" :key="`term-${i}`">
                  <a class="dropdown-item" href="#" @click.prevent="changeSelectedCategory(term)">
                    {{ term }}
                  </a>
                </li>
              </ul>
            </div>
          </div>
          <div class="col-lg-3">
            <div class="dropdown">
              <div
                id="dropdownMenuButtonDepartment"
                class="form-select"
                type="button"
                data-bs-toggle="dropdown"
                aria-expanded="false"
              >
                {{ selectedDepartment || 'Department' }}
              </div>
              <ul class="dropdown-menu" aria-labelledby="dropdownMenuButtonDepartment">
                <li key="Department">
                  <a class="dropdown-item" href="#" @click="changeSelectedDepartment('')"> Department </a>
                </li>
                <li v-for="department in departments" :key="department">
                  <a
                    class="dropdown-item"
                    href="#"
                    @click.prevent="changeSelectedDepartment(department)"
                  >
                    {{ department }}
                  </a>
                </li>
              </ul>
            </div>
          </div>
          <div class="col-lg-3">
            <div class="d-flex justify-content-between">
              <div class="search-input-container w-100">
                <IconMagnifyingGlass />
                <input
                  v-model="searchValue"
                  class="form-control"
                  type="text"
                  placeholder="Search for title, keyword..."
                  @input="debouncedSearch"
                  aria-label="Search for title, keyword..."
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="flex-shrink-0">
        <button
          class="btn btn-light clear-all flex-grow-1 text-nowrap align-self-center"
          style="padding-right: 2px"
          @click="clearFilters()"
        >
          Clear All
        </button>
      </div>
    </div>

    <div class="d-flex justify-content-between mb-2 align-items-center" style="height: 44px">
      <div v-if="openPositionModal.items.length > 0" class="results">
        Showing {{ (openPositionModal.size * (paginationStore.currentPage - 1)) + 1 }} -
        {{
          (openPositionModal.size * paginationStore.currentPage) <= openPositionModal.totalResults
            ? openPositionModal.size * paginationStore.currentPage
            : openPositionModal.totalResults
        }}
        of {{ openPositionModal.totalResults }} results
      </div>
      <div v-else class="results"> 0 results </div>
      <div class="d-flex align-items-center">
        <span class="sort-dropdown-title" id="sort-dropdown-title">Sort by</span>
        <select
          v-model="selectedSortValue"
          class="btn btn-light sort-dropdown px-0 pe-2"
          @change="changeSortValue(selectedSortValue)"
          aria-labelledby="sort-dropdown-title"
        >
          <option value="asc" @click="changeSortValue('asc')">A to Z</option>
          <option value="desc" @click="changeSortValue('desc')">Z to A</option>
        </select>
        <div class="sort-buttons">
          <button
            v-element-hover="onGridBtnHovered"
            :class="{ 'sort-view sort-grid': true, active: isGrid }"
            @click="openPositionModal.setIsGrid(true)"
            aria-label="Show in Grid"
          >
            <IconSquaresFour v-if="isGrid || isGridBtnHovered" />
            <IconSquaresFourInactive v-else />
          </button>
          <button
            v-element-hover="onListBtnHovered"
            :class="{ 'sort-view sort-list': true, active: !isGrid }"
            @click="openPositionModal.setIsGrid(false)"
            aria-label="Show in List"
          >
            <IconRowsInactive v-if="!isGrid" />
            <template v-else>
              <IconRows v-if="!isListBtnHovered" />
              <IconRowsHovered v-else />
            </template>
          </button>
        </div>
      </div>
    </div>
  </section>
</template>
<script setup lang="ts">
import debounce from 'lodash/debounce'
import { ref } from 'vue'
import { useOpenPositionModal } from '~/stores/modals/open-position-modal'
import { vElementHover } from '@vueuse/components'

const openPositionModal = useOpenPositionModal()

const {
  selectedLocation,
  selectedCategory,
  selectedDepartment,
  selectedSortValue,
  searchValue,
  locations,
  careerCategories,
  departments,
  isGrid,
  totalResults
} = storeToRefs(openPositionModal)

const paginationStore = usePaginationStore()

const debouncedSearch = debounce(() => openPositionModal.setSearchValue(searchValue.value), 1000)

function changeSelectedLocation(location: string) {
  openPositionModal.setSelectedLocation(location)
}

function changeSelectedCategory(category: string) {
  openPositionModal.setSelectedCategory(category)
}

function changeSelectedDepartment(department: string) {
  openPositionModal.setSelectedDepartment(department)
}

function changeSortValue(value: string) {
  openPositionModal.setSortValue(value)
}

function clearFilters() {
  openPositionModal.clearFilters()
}

const isGridBtnHovered = ref(false)
function onGridBtnHovered(state: boolean) {
  isGridBtnHovered.value = state
}
const isListBtnHovered = ref(false)
function onListBtnHovered(state: boolean) {
  isListBtnHovered.value = state
}
</script>
